import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { getProfile, saveProfile } from "@/lib/api";
import { Loader2, ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";

const INTERESTS = ["Kathas", "Mantras", "Stotras", "Pooja Vidhi", "Vedanta", "Bhakti", "Jyotisha", "Yoga", "Temples", "Festivals", "Sanskrit", "Meditation"];

export default function Onboarding() {
  const { pick } = useLang();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ mobile: "", city: "", dob: "", gotra: "", interests: [], eshta_devata: "", kula_devata: "", sadhana_goal: "", namasmarana_goal: "" });
  const [busy, setBusy] = useState(false);
  const [earned, setEarned] = useState(0);

  useEffect(() => { if (ready && !user) navigate("/login"); }, [ready, user, navigate]);
  useEffect(() => { getProfile().then((p) => setForm((f) => ({ ...f, ...p, interests: p.interests || [] }))).catch(() => {}); }, []);

  const steps = [
    { key: "basics", title: pick("आप कहाँ से हैं?", "Where are you from?"), sub: pick("ताकि हम आपके निकट के आयोजन एवं सेवाएँ सुझा सकें।", "So we can suggest nearby events & services.") },
    { key: "interests", title: pick("आपकी रुचियाँ", "Your interests"), sub: pick("हम इनके अनुसार सामग्री एवं समूह सुझाएँगे।", "We'll tailor content & groups to these.") },
    { key: "devata", title: pick("आपके इष्ट एवं कुल देवता", "Your Eshta & Kula Devata"), sub: pick("यह वैकल्पिक है — जितना सहज हो साझा करें।", "Optional — share as much as feels right.") },
    { key: "goals", title: pick("आपके संकल्प", "Your sadhana goals"), sub: pick("एक लक्ष्य आपकी यात्रा को दिशा देता है।", "A goal gives your journey direction.") },
  ];

  const persist = async (extra = {}, silent = false) => {
    const data = { ...form, ...extra };
    setForm(data);
    try {
      const r = await saveProfile(data);
      if (!silent && r.points_awarded > 0) { setEarned((e) => e + r.points_awarded); toast(`+${r.points_awarded} sadhana points`, { description: pick("प्रोफ़ाइल पूर्ण करने हेतु धन्यवाद।", "Thanks for completing this step.") }); }
      return r;
    } catch { return null; }
  };

  const next = async () => { setBusy(true); await persist(); setBusy(false); setStep((s) => Math.min(s + 1, steps.length - 1)); };
  const finish = async () => {
    setBusy(true); await persist({ onboarding_completed: true });
    setBusy(false);
    toast(pick("प्रोफ़ाइल तैयार!", "Profile ready!"), { description: pick("आपकी साधना यात्रा आरंभ होती है।", "Your journey begins.") });
    navigate("/sadhana");
  };
  const skip = async () => { await persist({ onboarding_completed: true }, true); navigate("/"); };

  const toggleInterest = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));
  const input = "w-full border border-line bg-parchment px-3 py-2.5 text-ink focus:border-saffron focus:outline-none";

  const cur = steps[step];
  return (
    <div data-testid="onboarding-page" className="mx-auto max-w-xl px-5 py-12 md:px-8 md:py-16">
      <Seo path="/onboarding" title="Complete your profile — Dharmartha" description="Set up your Dharmartha profile." />
      <p className="overline-label text-gold">{pick("स्वागत है", "Welcome, sadhak")}</p>
      <h1 className="mt-2 font-serif-latin text-3xl text-ink">{pick("अपनी प्रोफ़ाइल बनाएँ", "Set up your profile")}</h1>
      <p className="mt-1 text-sm text-ink-3">{pick("प्रत्येक चरण पर साधना अंक अर्जित करें।", "Earn sadhana points for each step.")} {earned > 0 && <span className="text-saffron">· +{earned}</span>}</p>

      {/* Progress */}
      <div className="mt-5 flex gap-1.5">
        {steps.map((s, i) => <div key={s.key} className={`h-1.5 flex-1 rounded ${i <= step ? "bg-saffron" : "bg-line"}`} />)}
      </div>

      <div className="mt-8 border border-line bg-surface p-6" data-testid={`onboarding-step-${cur.key}`}>
        <h2 className="font-serif-latin text-xl text-ink">{cur.title}</h2>
        <p className="mt-1 text-sm text-ink-3">{cur.sub}</p>

        <div className="mt-5 space-y-3">
          {cur.key === "basics" && (<>
            <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="ob-mobile" placeholder={pick("मोबाइल नंबर", "Mobile number")} className={input} />
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="ob-city" placeholder={pick("आपका शहर", "Your city")} className={input} />
            <label className="block text-xs text-ink-3">{pick("जन्म तिथि", "Date of birth")}
              <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="ob-dob" className={`${input} mt-1`} />
            </label>
            <input value={form.gotra} onChange={(e) => setForm({ ...form, gotra: e.target.value })} data-testid="ob-gotra" placeholder={pick("गोत्र (वैकल्पिक)", "Gotra (optional)")} className={input} />
          </>)}
          {cur.key === "interests" && (
            <div className="flex flex-wrap gap-2" data-testid="ob-interests">
              {INTERESTS.map((i) => (
                <button key={i} type="button" onClick={() => toggleInterest(i)} data-testid={`ob-interest-${i}`}
                  className={`border px-3 py-1.5 text-sm transition-colors ${form.interests.includes(i) ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>
                  {i}
                </button>
              ))}
            </div>
          )}
          {cur.key === "devata" && (<>
            <input value={form.eshta_devata} onChange={(e) => setForm({ ...form, eshta_devata: e.target.value })} data-testid="ob-eshta" placeholder={pick("इष्ट देव (जैसे शिव, राम, देवी)", "Eshta Devata (e.g. Shiva, Rama, Devi)")} className={input} />
            <input value={form.kula_devata} onChange={(e) => setForm({ ...form, kula_devata: e.target.value })} data-testid="ob-kula" placeholder={pick("कुल देवता", "Kula Devata (family deity)")} className={input} />
          </>)}
          {cur.key === "goals" && (<>
            <input value={form.sadhana_goal} onChange={(e) => setForm({ ...form, sadhana_goal: e.target.value })} data-testid="ob-sadhana-goal" placeholder={pick("साधना लक्ष्य (जैसे नित्य पाठ)", "Sadhana goal (e.g. daily paath)")} className={input} />
            <input value={form.namasmarana_goal} onChange={(e) => setForm({ ...form, namasmarana_goal: e.target.value })} data-testid="ob-namasmarana-goal" placeholder={pick("नामस्मरण लक्ष्य (जैसे ११ माला प्रतिदिन)", "Namasmarana goal (e.g. 11 malas/day)")} className={input} />
          </>)}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button onClick={() => (step === 0 ? skip() : setStep((s) => s - 1))} data-testid="ob-back" className="inline-flex items-center gap-1.5 text-sm text-ink-3 hover:text-ink">
            {step === 0 ? pick("छोड़ें", "Skip for now") : <><ArrowLeft className="h-4 w-4" /> {pick("पीछे", "Back")}</>}
          </button>
          {step < steps.length - 1 ? (
            <button onClick={next} disabled={busy} data-testid="ob-next" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {pick("आगे", "Continue")} <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="ob-finish" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {pick("पूर्ण करें", "Finish")}
            </button>
          )}
        </div>
      </div>
      <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-ink-3"><Sparkles className="h-3.5 w-3.5 text-gold" /> {pick("आप इसे बाद में खाता पृष्ठ से भी पूर्ण कर सकते हैं।", "You can finish this anytime from your Account page.")}</p>
    </div>
  );
}
